import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { LayoutDashboard, Database, Store, Tag, Download, LogOut, Package } from "lucide-react";
import { setToken } from "@/lib/api";
import { NexKeyMark } from "@/components/nexkey-logo";

export const Route = createFileRoute("/admin")({
  component: AdminLayout,
});

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, exact: true },
  { to: "/admin/games", label: "Games", icon: Database },
  { to: "/admin/gift-cards", label: "Gift Cards", icon: Tag },
  { to: "/admin/shops", label: "Shops", icon: Store },
  { to: "/admin/offers", label: "Offers", icon: Tag },
  { to: "/admin/import", label: "Steam import", icon: Download },
  { to: "/admin/gamebillet", label: "GameBillet import", icon: Package },
  { to: "/admin/gamivo", label: "GAMIVO import", icon: Package },
  { to: "/admin/eneba", label: "ENEBA import", icon: Package },
  { to: "/admin/gamesplanet", label: "Gamesplanet import", icon: Package },
  { to: "/admin/gameboost", label: "GameBoost import", icon: Package },
  { to: "/admin/gaming-dragons", label: "Gaming Dragons import", icon: Package },
  { to: "/admin/gamersgate", label: "GamersGate import", icon: Package },
  { to: "/admin/electronic-first", label: "Electronic First import", icon: Package },
  { to: "/admin/cjs-cdkeys", label: "CJS CD Keys import", icon: Package },
];

function AdminLayout() {
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const t = window.localStorage.getItem("nexkey_token");
    if (!t && path !== "/admin/login") {
      navigate({ to: "/admin/login" as any, replace: true });
    } else {
      setReady(true);
    }
  }, [path, navigate]);

  if (path === "/admin/login") return <Outlet />;
  if (!ready) return <div className="grid min-h-screen place-items-center text-muted-foreground">Loading…</div>;

  return (
    <div className="flex min-h-screen">
      <aside className="hidden w-60 shrink-0 border-r border-border bg-surface/60 md:flex md:flex-col">
        <div className="flex h-16 items-center gap-2 border-b border-border px-4 font-display font-bold">
          <NexKeyMark className="h-9 w-9" eager />
          <span className="text-gradient">NexKey</span>
          <span className="ml-1 rounded bg-accent/30 px-1.5 py-0.5 text-[10px] uppercase">admin</span>
        </div>
        <nav className="flex-1 space-y-1 p-3">
          {NAV.map((n) => {
            const active = n.exact ? path === n.to : path.startsWith(n.to);
            return (
              <Link
                key={n.to}
                to={n.to as any}
                className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm ${active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-surface hover:text-foreground"}`}
              >
                <n.icon className="h-4 w-4" /> {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="border-t border-border p-3">
          <button
            onClick={() => { setToken(null); navigate({ to: "/admin/login" as any, replace: true }); }}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-destructive/15 hover:text-destructive"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
          <Link to="/" className="mt-2 block px-3 py-1 text-center text-xs text-muted-foreground hover:text-primary">← Back to site</Link>
        </div>
      </aside>
      <main className="flex-1 overflow-x-hidden p-4 md:p-8">
        <Outlet />
      </main>
    </div>
  );
}
